/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { Drawer } from '@base-ui/react/drawer';

import { UIcon } from 'shared-ui/uicon';
import { SafariToolbarColorFixer } from 'shared-ui/utils/SafariToolbarColorFixer';

import { DatePickerTrigger } from './DatePickerTrigger';
import type { DatePickerShell } from './DatePicker.types';
import type { FlexDatePickerSize } from './FlexDatePicker.types';

import styles from './DatePickerDrawer.module.scss';

interface DatePickerDrawerProps {
    shell: DatePickerShell;
    /** The picker body rendered inside the drawer. */
    children: React.ReactNode;
    isDisabled?: boolean;
    clickTagCategory?: string;
    size?: FlexDatePickerSize;
    /** Drawer title while nothing is selected. */
    title?: string;
    /** Screen-reader prefix that names the trigger. */
    triggerLabel?: string;
    /** Accessible name of the trigger's clear button. */
    clearLabel?: string;
}

/**
 * Bottom-sheet wrapper for small viewports. Closing by any means (swipe,
 * backdrop, close button, or Apply) commits the draft, matching the popover.
 */
export function DatePickerDrawer({
    shell,
    children,
    isDisabled,
    clickTagCategory,
    size,
    title = 'Choose Dates',
    triggerLabel,
    clearLabel,
}: DatePickerDrawerProps) {
    const { isOpen, onOpenChange, drawerTitleContent, currentTabHasDates } = shell;

    return (
        <Drawer.Root open={isOpen} onOpenChange={onOpenChange}>
            <DatePickerTrigger
                TriggerPart={Drawer.Trigger}
                shell={shell}
                isDisabled={isDisabled}
                clickTagCategory={clickTagCategory}
                size={size}
                label={triggerLabel}
                clearLabel={clearLabel}
            />
            <Drawer.Portal className={styles.Portal}>
                <Drawer.Backdrop className={styles.Backdrop} />
                <Drawer.Viewport className={styles.Viewport}>
                    <Drawer.Popup className={styles.Popup}>
                        <div className={styles.Handle} aria-hidden="true" />
                        <Drawer.Content className={styles.Content}>
                            <div className={styles.Header}>
                                {/* Repeats the draft selection, since one calendar month can't show a longer range. */}
                                <Drawer.Title className={styles.Title}>
                                    {currentTabHasDates ? drawerTitleContent : title}
                                </Drawer.Title>
                                <Drawer.Close aria-label="Close" className={styles.Close}>
                                    <UIcon name="x" size={20} />
                                </Drawer.Close>
                            </div>
                            {children}
                        </Drawer.Content>
                    </Drawer.Popup>
                </Drawer.Viewport>
            </Drawer.Portal>
            {/* Correctly tint Safari's toolbar */}
            {isOpen && <SafariToolbarColorFixer cssColorVar="--color-ui-03" />}
        </Drawer.Root>
    );
}
